import type { LucideIcon } from 'lucide-react';
import type { ComponentType } from 'react';
import { cn } from '@Utils';

export type FullPageStatusTone = 'brand' | 'error' | 'warning' | 'success';

const TONE_CLASSES: Record<FullPageStatusTone, { watermark: string; badge: string; badgeIcon: string }> = {
  brand: {
    watermark: 'text-brand-500/10',
    badge: 'bg-brand-50',
    badgeIcon: 'text-brand-500',
  },
  error: {
    watermark: 'text-error-500/10',
    badge: 'bg-error-50',
    badgeIcon: 'text-error-600',
  },
  warning: {
    watermark: 'text-warning-500/10',
    badge: 'bg-warning-50',
    badgeIcon: 'text-warning-600',
  },
  success: {
    watermark: 'text-success-500/10',
    badge: 'bg-success-50',
    badgeIcon: 'text-success-600',
  },
};

export interface FullPageStatusAction {
  label: string;
  /** Optional leading icon. */
  icon?: LucideIcon;
  /** Primary actions are filled with brand color, secondary are outline. */
  variant?: 'primary' | 'secondary';
  onClick: () => void;
}

export interface FullPageStatusProps {
  /** Big translucent string behind the icon — e.g. "404", "500", "SOON". */
  watermark: string;
  /** Tonal palette used for watermark + badge. Defaults to brand. */
  tone?: FullPageStatusTone;
  /** Lucide icon rendered inside the rounded badge. */
  icon: LucideIcon | ComponentType<{ className?: string }>;
  /** Main heading. */
  title: string;
  /** Optional supporting text. Honors `\n` for line breaks. */
  description?: string;
  /** Up to two action buttons rendered below the description. */
  actions?: FullPageStatusAction[];
}

/**
 * Reusable full-page status surface used by NotFound (404), ErrorMessage (500)
 * and WorkInProgress ("SOON"). Centered card with a tonal watermark behind a
 * rounded icon badge, then title + description, then optional action buttons.
 */
const FullPageStatus = ({
  watermark,
  tone = 'brand',
  icon: Icon,
  title,
  description,
  actions = [],
}: FullPageStatusProps) => {
  const styles = TONE_CLASSES[tone];

  return (
    <div className="relative flex h-full w-full flex-col">
      <div className="m-auto flex max-w-lg flex-col items-center px-6 text-center">
        <div
          className={cn(
            'pointer-events-none mb-[-32px] select-none text-[9rem] font-extrabold leading-none tracking-tighter',
            styles.watermark,
          )}>
          {watermark}
        </div>
        <div
          className={cn('mb-5 flex h-14 w-14 items-center justify-center rounded-2xl', styles.badge, styles.badgeIcon)}>
          <Icon className="h-7 w-7" />
        </div>
        <h1 className="m-0 text-2xl font-semibold tracking-tight text-gray-900">{title}</h1>
        {description && (
          <p className="mt-3 whitespace-pre-line text-[0.95rem] leading-relaxed text-gray-500">{description}</p>
        )}
        {actions.length > 0 && (
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            {actions.map((action) => {
              const isPrimary = action.variant === 'primary';
              const ActionIcon = action.icon;
              return (
                <button
                  key={action.label}
                  type="button"
                  onClick={action.onClick}
                  className={cn(
                    'inline-flex h-11 items-center gap-2 rounded-xl px-5 text-[0.95rem] font-medium transition',
                    isPrimary
                      ? 'bg-brand-500 text-white shadow-sm hover:bg-brand-600'
                      : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50',
                  )}>
                  {ActionIcon && <ActionIcon className="h-4 w-4" />}
                  {action.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FullPageStatus;
